import { useEffect, useRef, useState } from 'react';
import * as audienceApi from '../api/audience';
import { useToast } from './toast';
import { Spinner, ErrorAlert } from './ui';
import { cn, ui } from '../lib/ui';
import {
  AUDIENCE_COPY,
  AUDIENCE_FIELDS,
  DEFAULT_AUDIENCE_CONFIG,
  COUNT_LIMITS,
  normalizeAudienceConfig,
  validateAudienceConfig,
} from '../lib/audience';
import { fieldErrorsFrom, focusFirstInvalid } from '../lib/formErrors';

/** The plan feature each section needs; a section the plan lacks is not shown. */
const PLAN_FEATURE = { events: 'events', videos: 'video-hosting' };

function defaultText(kind, value) {
  if (kind === 'boolean') return value ? 'On' : 'Off';
  return value === 0 ? 'Unlimited (0)' : String(value);
}

/** Form state keeps counts as typed text, so a half-typed or empty value can be flagged. */
function toDraft(config) {
  const draft = {};
  for (const [feature, fields] of Object.entries(AUDIENCE_FIELDS)) {
    draft[feature] = {};
    for (const [field, kind] of Object.entries(fields)) {
      const value = config[feature][field];
      draft[feature][field] = kind === 'count' ? String(value) : value;
    }
  }
  return draft;
}

function fromDraft(draft, features) {
  const body = {};
  for (const feature of features) {
    body[feature] = {};
    for (const [field, kind] of Object.entries(AUDIENCE_FIELDS[feature])) {
      const raw = draft[feature][field];
      body[feature][field] = kind === 'count' ? (raw.trim() === '' ? NaN : Number(raw)) : raw;
    }
  }
  return body;
}

function Toggle({ id, checked, onChange, invalid }) {
  return (
    <input
      id={id}
      type="checkbox"
      className={cn(ui.checkbox, 'mt-1 shrink-0', invalid && 'outline-2 outline-dcon-danger')}
      checked={checked}
      onChange={e => onChange(e.target.checked)}
      aria-invalid={invalid || undefined}
    />
  );
}

/**
 * The organization's audience settings (docs/Decisions.md D63 item 2): one
 * collapsed section per feature the plan includes, each setting with what it
 * does and its default. Errors, from this form or from the API, open their
 * section and sit beside their field (D63 item 3).
 *
 * Props: orgId; planFeatures (the org's features, or null for every section);
 * onSaved(config) after a save; renderActions({ saving, save }) for the
 * buttons (the wizard adds Back and Skip); saveLabel.
 */
export default function AudienceSettingsForm({
  orgId,
  planFeatures = null,
  onSaved,
  renderActions,
  saveLabel = 'Save',
}) {
  const toast = useToast();
  const rootRef = useRef(null);
  const [draft, setDraft] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [errors, setErrors] = useState({});
  const [open, setOpen] = useState({});
  const [saving, setSaving] = useState(false);

  const features = Object.keys(AUDIENCE_FIELDS).filter(
    f => !planFeatures || planFeatures.includes(PLAN_FEATURE[f])
  );

  useEffect(() => {
    let cancelled = false;
    audienceApi
      .getAudienceConfig(orgId)
      .then(data => {
        if (!cancelled) setDraft(toDraft(normalizeAudienceConfig(data.audienceConfig || data)));
      })
      .catch(err => {
        if (!cancelled) setLoadError(err);
      });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  function setField(feature, field, value) {
    setDraft(prev => ({ ...prev, [feature]: { ...prev[feature], [field]: value } }));
    setErrors(prev => {
      const next = { ...prev };
      delete next[`${feature}.${field}`];
      return next;
    });
  }

  function resetSection(feature) {
    const defaults = toDraft(DEFAULT_AUDIENCE_CONFIG)[feature];
    setDraft(prev => ({ ...prev, [feature]: defaults }));
    setErrors(prev =>
      Object.fromEntries(Object.entries(prev).filter(([k]) => !k.startsWith(`${feature}.`)))
    );
  }

  function showErrors(found) {
    setErrors(found);
    const withErrors = Object.keys(found).map(k => k.split('.')[0]);
    setOpen(prev => ({ ...prev, ...Object.fromEntries(withErrors.map(f => [f, true])) }));
    focusFirstInvalid(rootRef.current);
  }

  async function save() {
    const body = fromDraft(draft, features);
    const { fieldErrors } = validateAudienceConfig(body);
    if (Object.keys(fieldErrors).length) {
      showErrors(fieldErrors);
      toast.error('Some settings need attention - see the highlighted fields');
      return false;
    }
    setSaving(true);
    try {
      const data = await audienceApi.putAudienceConfig(orgId, body);
      setErrors({});
      toast.success('Feature settings saved');
      onSaved?.(data.audienceConfig);
      return true;
    } catch (err) {
      const found = fieldErrorsFrom(err);
      if (Object.keys(found).length) showErrors(found);
      toast.error(err.message || 'Failed to save feature settings');
      return false;
    } finally {
      setSaving(false);
    }
  }

  if (loadError) return <ErrorAlert error={loadError} />;
  if (!draft) return <Spinner label="Loading feature settings..." />;

  return (
    <div ref={rootRef}>
      {features.length === 0 && (
        <p className={cn(ui.muted, 'text-body-s')}>
          Your plan has no organization-wide feature settings to choose.
        </p>
      )}
      {features.map(feature => {
        const copy = AUDIENCE_COPY[feature];
        const sectionErrors = Object.keys(errors).filter(k => k.startsWith(`${feature}.`));
        return (
          <details
            key={feature}
            className={cn(
              'mb-3 rounded-z-card border bg-z-surface',
              sectionErrors.length ? 'border-2 border-dcon-danger' : 'border-z-line'
            )}
            open={Boolean(open[feature])}
            onToggle={e => {
              const isOpen = e.currentTarget.open;
              setOpen(prev => (prev[feature] === isOpen ? prev : { ...prev, [feature]: isOpen }));
            }}
          >
            <summary className="flex cursor-pointer items-center justify-between gap-3 p-4">
              <span>
                <span className="block font-semibold">{copy.title}</span>
                <span className={cn(ui.muted, 'text-body-s')}>{copy.summary}</span>
              </span>
              {sectionErrors.length > 0 && (
                <span className={cn(ui.pillDanger, 'shrink-0 whitespace-nowrap')}>
                  {sectionErrors.length === 1 ? '1 to fix' : `${sectionErrors.length} to fix`}
                </span>
              )}
            </summary>
            <div className="border-t border-z-line p-4">
              {Object.entries(AUDIENCE_FIELDS[feature]).map(([field, kind]) => {
                const key = `${feature}.${field}`;
                const id = `aud-${feature}-${field}`;
                const fieldCopy = copy.fields[field];
                const error = errors[key];
                const descId = `${id}-desc`;
                const errId = `${id}-err`;
                const def = DEFAULT_AUDIENCE_CONFIG[feature][field];
                return (
                  <div className="mb-4 last:mb-0" key={field}>
                    <div className="flex items-start gap-3">
                      {kind === 'boolean' && (
                        <Toggle
                          id={id}
                          checked={draft[feature][field]}
                          onChange={v => setField(feature, field, v)}
                          invalid={Boolean(error)}
                        />
                      )}
                      <div className="flex-1">
                        <label className="font-semibold text-body-s" htmlFor={id}>
                          {fieldCopy.label}
                        </label>
                        <p className={cn(ui.hint, 'mt-0')} id={descId}>
                          {fieldCopy.description} Default: {defaultText(kind, def)}.
                        </p>
                        {kind === 'count' && (
                          <input
                            id={id}
                            type="number"
                            inputMode="numeric"
                            min="0"
                            max={COUNT_LIMITS[field].max}
                            step="1"
                            className={cn(ui.input, 'mt-1 w-32!', error && ui.inputInvalid)}
                            value={draft[feature][field]}
                            onChange={e => setField(feature, field, e.target.value)}
                            aria-invalid={error ? true : undefined}
                            aria-describedby={error ? `${descId} ${errId}` : descId}
                          />
                        )}
                        {error && (
                          <span className={ui.fieldError} id={errId} role="alert">
                            {error}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <button
                type="button"
                className={cn(ui.btnGhost, ui.btnSm, 'mt-2')}
                onClick={() => resetSection(feature)}
              >
                Use the defaults for {copy.title.toLowerCase()}
              </button>
            </div>
          </details>
        );
      })}
      {errors._ && <ErrorAlert message={errors._} />}
      {renderActions ? (
        renderActions({ saving, save })
      ) : (
        <div className={cn(ui.row, 'mt-4')}>
          <button className={ui.btnPrimary} onClick={save} disabled={saving} type="button">
            {saving ? 'Saving...' : saveLabel}
          </button>
        </div>
      )}
    </div>
  );
}
